import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatPoints, timeLeft } from "@/lib/format";
import { formatPesos, getSettlementPcts } from "@/lib/currency";
import InsufficientBalanceModal from "@/components/InsufficientBalanceModal";
import {
    Clock, Users, ArrowLeft, Zap, Trophy, Ticket, Info, Shield,
    Gift, MessageCircle, Mic, FileText, ChevronRight,
} from "lucide-react";

const PRIMARY = "bg-p2w-blue text-white hover:bg-p2w-blue/90 shadow-[0_0_24px_rgba(59,130,246,0.35)]";

export default function MarketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const { t, lang } = useI18n();
    const [market, setMarket] = useState(null);
    const [selected, setSelected] = useState(null);
    const [stake, setStake] = useState(5000);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [stakeOpen, setStakeOpen] = useState(false);
    const [insufficientOpen, setInsufficientOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/markets/${id}`);
            setMarket(data);
            // Solo asignar opción por defecto en la PRIMERA carga; nunca pisar la elección del usuario.
            setSelected((prev) => prev || data.options?.[0]?.id || null);
        } catch (e) {
            toast.error("No se pudo cargar el mercado");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 12000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Resume saved bet intent (after recargar)
    useEffect(() => {
        if (!market || !user || user === false) return;
        try {
            const raw = localStorage.getItem("p2w-bet-intent");
            if (!raw) return;
            const intent = JSON.parse(raw);
            if (Date.now() - (intent.ts || 0) > 30 * 60 * 1000) {
                localStorage.removeItem("p2w-bet-intent");
                return;
            }
            if (intent.market_id === market.id) {
                if (intent.option_id) setSelected(intent.option_id);
                if (intent.stake) setStake(intent.stake);
                localStorage.removeItem("p2w-bet-intent");
                toast.message("Retomamos tu apuesta. Confirma cuando estés listo.");
                setStakeOpen(true);
            }
        } catch { /* noop */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [market?.id, user]);

    if (loading || !market) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
                <div className="h-96 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" />
            </div>
        );
    }

    const isLoggedIn = user && user !== false;
    const isClosed = market.status !== "open" || market.is_closed;
    const totalParticipants = market.options.reduce((s, o) => s + (o.participants || 0), 0);
    const opt = market.options.find((o) => o.id === selected) || market.options[0];
    const pcts = market.settlement
        ? {
              players: market.settlement.players_pct,
              platform: market.settlement.platform_pct,
              creator: market.settlement.creator_pct,
              league: market.settlement.league_pct,
          }
        : getSettlementPcts();

    const stakeNum = Math.max(0, Number(stake) || 0);
    const pozoAfter = market.total_pool + stakeNum;
    const distributableAfter = pozoAfter * pcts.players;
    const winningPoolAfter = (opt?.current_pool || 0) + stakeNum;
    const rawMultiplier = winningPoolAfter > 0 ? distributableAfter / winningPoolAfter : 0;
    // Garantía: el ganador siempre recibe al menos stake × 1.01 (1% mínimo de ganancia).
    // Si el cálculo crudo es menor, la plataforma absorbe el déficit (modelo de protección al apostador).
    const MIN_MULTIPLIER = 1.01;
    const multiplierAfter = Math.max(MIN_MULTIPLIER, rawMultiplier);
    const minPayout = stakeNum > 0 ? Math.ceil(stakeNum * MIN_MULTIPLIER) : 0;
    const rawPayout = stakeNum > 0 ? Math.round(stakeNum * rawMultiplier) : 0;
    const potentialPayout = Math.max(minPayout, rawPayout);
    const netGain = Math.max(0, potentialPayout - stakeNum);
    const lowLiquidity = rawMultiplier < MIN_MULTIPLIER && stakeNum > 0;
    const playersAmount = market.settlement?.players_amount ?? Math.round(market.total_pool * pcts.players);

    const openPredictDialog = () => {
        if (!isLoggedIn) {
            toast.message(t("market.login_required"));
            navigate("/login");
            return;
        }
        if (isClosed) return;
        setStakeOpen(true);
    };

    const onSubmit = async () => {
        if (!selected || stakeNum <= 0) return;
        if (stakeNum > (user.points_balance || 0)) {
            setStakeOpen(false);
            setInsufficientOpen(true);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/markets/${market.id}/predict`, { option_id: selected, stake_points: stakeNum });
            toast.success(t("market.success_placed"));
            setStakeOpen(false);
            await refresh();
            await load();
        } catch (e) {
            const detail = formatApiErrorDetail(e.response?.data?.detail) || "Error al predecir";
            if (typeof detail === "string" && detail.toLowerCase().includes("insuficiente")) {
                setStakeOpen(false);
                setInsufficientOpen(true);
            } else {
                toast.error(detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-32" data-testid="market-detail">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-p2w-gray hover:text-white transition mb-5" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /> Volver
            </button>

            <div className="grid lg:grid-cols-3 gap-5">
                {/* ============ LEFT ============ */}
                <div className="lg:col-span-2 space-y-5">
                    {/* HERO CARD */}
                    <div className="relative overflow-hidden rounded-2xl border border-p2w-line bg-p2w-surface">
                        <div className="relative h-56 sm:h-72">
                            {market.image_url && (
                                <img src={market.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-p2w-surface via-p2w-surface/40 to-transparent" />
                            <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-p2w-amber/15 border border-p2w-amber/40 backdrop-blur" data-testid="quick-pool-badge">
                                <Zap className="w-3.5 h-3.5 text-p2w-amber" />
                                <span className="text-[10px] uppercase tracking-[0.25em] font-sora font-extrabold text-p2w-amber">QUICK POOL</span>
                            </div>
                        </div>
                        <div className="px-6 pb-6">
                            <h1 className="font-sora font-extrabold text-2xl sm:text-3xl text-white leading-tight tracking-tight -mt-2">
                                {market.title}
                            </h1>
                            <div className="mt-5 flex flex-wrap gap-2.5">
                                <StatPill icon={Trophy} value={formatPesos(playersAmount)} color="green" testid="pill-prize" />
                                <StatPill icon={Ticket} value={formatPesos(5000)} color="red" testid="pill-entry" />
                                <StatPill icon={Users} value={`${market.participants || 0}`} color="gray" testid="pill-players" />
                                <StatPill icon={Clock} value={timeLeft(market.closes_at, lang)} color="amber" testid="pill-closes" />
                            </div>
                            <SentimentBar market={market} totalParticipants={totalParticipants} />
                        </div>
                    </div>

                    {/* TABS row */}
                    <Tabs defaultValue="prediccion">
                        <TabsList className="bg-p2w-elevated border border-p2w-line p-1 h-auto flex-wrap" data-testid="market-tabs">
                            <TabsTrigger value="prediccion" data-testid="tab-prediccion" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-1.5 px-4 py-2">
                                <Trophy className="w-3.5 h-3.5" /> Predicción
                            </TabsTrigger>
                            <TabsTrigger value="evidencias" data-testid="tab-evidencias" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-1.5 px-4 py-2">
                                <Zap className="w-3.5 h-3.5" /> Evidencias
                            </TabsTrigger>
                            <TabsTrigger value="chat" data-testid="tab-chat" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-1.5 px-4 py-2">
                                <MessageCircle className="w-3.5 h-3.5" /> Chat
                            </TabsTrigger>
                            <TabsTrigger value="voz" data-testid="tab-voz" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-1.5 px-4 py-2">
                                <Mic className="w-3.5 h-3.5" /> Voz
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="prediccion" className="mt-4 space-y-4">
                            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-p2w-elevated border border-p2w-line">
                                <Clock className="w-4 h-4 text-p2w-gray" />
                                <span className="text-sm text-white">Cierra en: </span>
                                <span className="text-sm font-sora font-bold text-p2w-green number-tabular">{timeLeft(market.closes_at, lang)}</span>
                            </div>

                            <Accordion type="multiple" defaultValue={["how", "breakdown"]} className="space-y-3">
                                {market.description && (
                                    <AccordionItem value="desc" className="rounded-2xl border border-p2w-line bg-p2w-surface px-5 data-[state=open]:bg-p2w-surface">
                                        <AccordionTrigger data-testid="accordion-desc" className="hover:no-underline">
                                            <span className="flex items-center gap-2 font-sora font-bold text-white">
                                                <Info className="w-4 h-4 text-p2w-gray" /> Descripción del pool
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent className="text-p2w-gray text-sm leading-relaxed pb-5">
                                            {market.description}
                                        </AccordionContent>
                                    </AccordionItem>
                                )}

                                <AccordionItem value="how" className="rounded-2xl border border-p2w-line bg-p2w-surface px-5">
                                    <AccordionTrigger data-testid="accordion-how" className="hover:no-underline">
                                        <span className="flex items-center gap-2 font-sora font-bold text-white">
                                            <Zap className="w-4 h-4 text-p2w-amber" /> Cómo funciona este Quick Pool
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent className="text-p2w-gray text-sm space-y-2.5 pb-5">
                                        <p>🎯 Predice qué opción ganará antes del cierre del pool.</p>
                                        <p>🏆 El <span className="text-p2w-green font-bold">{Math.round(pcts.players * 100)}%</span> del pozo se reparte entre todos los que acertaron, proporcionalmente al stake.</p>
                                        <p>🎟️ Puedes apostar múltiples veces para aumentar tu participación.</p>
                                        <p>🔒 Cada predicción es privada. Nadie ve cuánto apostaste.</p>
                                        <p>⚡ El resultado se resuelve por el equipo Play2Win con evidencia verificable.</p>
                                    </AccordionContent>
                                </AccordionItem>

                                <AccordionItem value="breakdown" className="rounded-2xl border border-p2w-line bg-p2w-surface px-5" data-testid="pool-breakdown">
                                    <AccordionTrigger data-testid="accordion-breakdown" className="hover:no-underline">
                                        <span className="flex items-center gap-2 font-sora font-bold text-white">
                                            <Shield className="w-4 h-4 text-p2w-green" /> Distribución del pozo · Transparencia
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-5">
                                        <div className="flex items-center justify-between py-3 border-b border-p2w-line">
                                            <span className="text-sm text-p2w-gray">Total recaudado</span>
                                            <span className="font-sora font-extrabold text-xl text-white number-tabular">
                                                {formatPesos(market.total_pool)}
                                            </span>
                                        </div>
                                        <BreakdownLine icon="🏆" pct={pcts.players} amount={playersAmount} label="Jugadores" color="text-p2w-green" testid="breakdown-players" />
                                        <BreakdownLine icon="🎙️" pct={pcts.creator} amount={market.settlement?.creator_amount ?? Math.round(market.total_pool * pcts.creator)} label="Creador" color="text-p2w-purple" testid="breakdown-creator" />
                                        <BreakdownLine icon="🥇" pct={pcts.league} amount={market.settlement?.league_amount ?? Math.round(market.total_pool * pcts.league)} label="Liga Mensual" color="text-p2w-amber" testid="breakdown-league" />
                                        <BreakdownLine icon="⚡" pct={pcts.platform} amount={market.settlement?.platform_amount ?? Math.round(market.total_pool * pcts.platform)} label="Play2Win" color="text-p2w-gray" testid="breakdown-platform" />
                                        <p className="mt-3 text-[11px] text-p2w-gray">
                                            {Math.round(pcts.players*100)}% jugadores · {Math.round(pcts.platform*100)}% plataforma · {Math.round(pcts.creator*100)}% creador · {Math.round(pcts.league*100)}% liga
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </TabsContent>

                        <TabsContent value="evidencias" className="mt-4">
                            <ComingSoon icon={FileText} title="Evidencias" desc="Aquí aparecerán las pruebas verificables para la resolución del pool." />
                        </TabsContent>
                        <TabsContent value="chat" className="mt-4">
                            <ComingSoon icon={MessageCircle} title="Chat del pool" desc="Conversación con los demás predictores. Próximamente." />
                        </TabsContent>
                        <TabsContent value="voz" className="mt-4">
                            <ComingSoon icon={Mic} title="Sala de voz" desc="Debate en vivo con la comunidad. Próximamente." />
                        </TabsContent>
                    </Tabs>
                </div>

                {/* ============ RIGHT ============ */}
                <div className="lg:sticky lg:top-24 h-fit space-y-4">
                    <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5">
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-sora font-bold text-white text-sm">Distribución live</span>
                            <div className="flex items-center gap-1.5 text-xs text-p2w-green">
                                <span className="w-1.5 h-1.5 rounded-full bg-p2w-green animate-pulse-dot" />
                                <span className="number-tabular">{market.participants || 0} jugadores</span>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {market.options.map((o) => {
                                const pct = totalParticipants > 0 ? ((o.participants || 0) / totalParticipants) * 100 : 0;
                                return (
                                    <div key={o.id}>
                                        <div className="flex items-center justify-between text-sm mb-1.5">
                                            <span className="text-white truncate pr-2">{o.label}</span>
                                            <span className="font-sora font-bold text-white number-tabular">{pct.toFixed(0)}%</span>
                                        </div>
                                        <div className="h-1.5 bg-p2w-elevated rounded-full overflow-hidden">
                                            <div className="h-full bg-p2w-blue" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-p2w-gray mb-3">Opciones</div>
                        <div className="space-y-2.5">
                            {market.options.map((o) => {
                                const pct = totalParticipants > 0 ? ((o.participants || 0) / totalParticipants) * 100 : 0;
                                const moneyOnOption = formatPesos(o.current_pool);
                                const isSel = selected === o.id;
                                const isWinner = market.result_option_id === o.id;
                                // Estimador: si apostaras un stake típico (1000 pts), cuánto recibirías
                                const ESTIMATE_STAKE = 1000;
                                const poolAfterEst = market.total_pool + ESTIMATE_STAKE;
                                const winPoolAfterEst = (o.current_pool || 0) + ESTIMATE_STAKE;
                                const rawEst = winPoolAfterEst > 0 ? (poolAfterEst * pcts.players) / winPoolAfterEst : 0;
                                const multEst = Math.max(MIN_MULTIPLIER, rawEst);
                                const payoutEst = Math.max(Math.ceil(ESTIMATE_STAKE * MIN_MULTIPLIER), Math.round(ESTIMATE_STAKE * rawEst));
                                return (
                                    <button
                                        key={o.id}
                                        onClick={() => !isClosed && setSelected(o.id)}
                                        data-testid={`option-${o.id}`}
                                        disabled={isClosed}
                                        className={`w-full text-left rounded-xl border px-4 py-3 transition flex items-center justify-between gap-3 ${
                                            isWinner
                                                ? "border-p2w-green bg-p2w-green/15"
                                                : isSel
                                                ? "border-p2w-blue/60 bg-p2w-blue/10"
                                                : "border-p2w-line bg-p2w-elevated hover:border-p2w-blue/40"
                                        } ${isClosed && !isWinner ? "opacity-60 cursor-not-allowed" : ""}`}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${isSel || isWinner ? "bg-p2w-green" : "bg-p2w-blue"}`} />
                                            <div className="min-w-0">
                                                <div className="font-semibold text-white truncate">{o.label}</div>
                                                <div className="text-[10px] text-p2w-gray number-tabular">
                                                    {o.participants || 0} votos · <span className="text-p2w-green">{moneyOnOption}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-sora font-bold text-white text-base number-tabular leading-none">×{multEst.toFixed(2)}</div>
                                            <div className="text-[9px] uppercase tracking-wider text-p2w-gray mt-0.5">
                                                1.000 → <span className="text-p2w-green font-semibold number-tabular">{formatPoints(payoutEst)}</span>
                                            </div>
                                            <div className="text-[9px] text-p2w-gray/60 number-tabular">{pct.toFixed(0)}% de votos</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <Button
                            data-testid="confirm-prediction-btn"
                            onClick={openPredictDialog}
                            disabled={submitting || isClosed}
                            className={`w-full mt-4 ${PRIMARY} font-semibold py-6 rounded-xl text-base group`}
                        >
                            {isClosed ? "Mercado cerrado" : isLoggedIn ? "Únete para predecir" : t("market.login_required")}
                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </div>

                    <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5" data-testid="market-distribution-card">
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-p2w-gray">
                                Distribución de votos
                            </div>
                            <span className="text-[10px] text-p2w-gray">
                                {totalParticipants} {totalParticipants === 1 ? "voto" : "votos"}
                            </span>
                        </div>
                        <OptionsDonut options={market.options} totalParticipants={totalParticipants} />
                    </div>
                </div>
            </div>

            {/* ============ STICKY BOTTOM BAR ============ */}
            <div className="fixed bottom-0 inset-x-0 z-30 border-t border-p2w-line bg-p2w-bg/95 backdrop-blur-xl" data-testid="sticky-bar">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-5 sm:gap-7">
                        <FooterStat label="entrada" value={formatPesos(5000)} color="text-white" />
                        <FooterStat label="pozo" value={formatPesos(market.total_pool)} color="text-p2w-green" />
                        <FooterStat label="jugadores" value={`${market.participants || 0}`} color="text-white" hideOnMobile />
                        <FooterStat label="cierre" value={timeLeft(market.closes_at, lang)} color="text-p2w-amber" hideOnMobile />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button data-testid="rewards-btn" onClick={() => navigate("/rankings")} className="bg-p2w-amber text-p2w-bg hover:bg-p2w-amber/90 font-semibold gap-1.5 rounded-xl">
                            <Gift className="w-4 h-4" /> Recompensas
                        </Button>
                        <Button data-testid="participate-btn" onClick={openPredictDialog} className={`${PRIMARY} font-semibold rounded-xl`}>
                            Participar <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* ============ STAKE DIALOG ============ */}
            <Dialog open={stakeOpen} onOpenChange={setStakeOpen}>
                <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                    <DialogHeader>
                        <DialogTitle className="font-sora">Confirmar tu predicción</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="px-4 py-3 rounded-xl bg-p2w-elevated border border-p2w-line">
                            <div className="text-[10px] uppercase tracking-widest text-p2w-gray">Tu opción</div>
                            <div className="mt-1 font-sora font-bold text-lg text-p2w-blue">{opt?.label}</div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-p2w-gray">Saldo</span>
                            <span className="font-sora font-bold text-p2w-green number-tabular">
                                {formatPoints(user?.points_balance || 0)} pts <span className="text-[10px] text-p2w-gray ml-1">= {formatPesos(user?.points_balance || 0)}</span>
                            </span>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-p2w-gray">Cantidad a apostar</label>
                            <Input type="number" data-testid="stake-input" value={stake} min={1} onChange={(e) => setStake(e.target.value)} className="mt-2 bg-p2w-elevated border-p2w-line text-white text-xl font-sora font-bold focus-visible:ring-p2w-blue/40" />
                            <div className="mt-2 grid grid-cols-4 gap-2">
                                {[1000, 5000, 10000, "max"].map((v) => (
                                    <button key={v} onClick={() => setStake(v === "max" ? (user?.points_balance || 0) : v)} className="text-xs px-2 py-1.5 rounded-md border border-p2w-line bg-p2w-elevated text-p2w-gray hover:text-p2w-blue hover:border-p2w-blue/40 transition">
                                        {v === "max" ? "MAX" : v}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-xl bg-gradient-to-br from-p2w-elevated to-p2w-elevated/40 border border-p2w-line overflow-hidden">
                            {/* HERO: Valor a reclamar (lo más visible) */}
                            <div className="p-5 bg-gradient-to-br from-p2w-green/10 via-transparent to-transparent">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-p2w-gray font-semibold">Si aciertas, recibes</div>
                                        <div className="mt-1 flex items-baseline gap-2">
                                            <span className="font-sora font-extrabold text-p2w-green text-3xl number-tabular tracking-tight" data-testid="potential-payout">
                                                {formatPoints(potentialPayout)}
                                            </span>
                                            <span className="text-sm text-p2w-gray font-semibold">pts</span>
                                        </div>
                                        <div className="text-[11px] text-p2w-gray mt-0.5">≈ {formatPesos(potentialPayout)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider text-p2w-gray">Multiplicador</div>
                                        <div className="font-sora font-bold text-white text-xl number-tabular" data-testid="potential-multiplier">×{multiplierAfter.toFixed(2)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Breakdown: stake + ganancia */}
                            <div className="px-5 py-3 border-t border-p2w-line bg-p2w-bg/40 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-p2w-gray">
                                    <span className="number-tabular">{formatPoints(stakeNum)}</span>
                                    <span className="text-p2w-gray/60">apuesta</span>
                                    <span className="text-p2w-gray/40">+</span>
                                    <span className={`number-tabular font-semibold ${netGain > 0 ? "text-p2w-green" : "text-p2w-gray"}`} data-testid="potential-net-gain">
                                        {formatPoints(netGain)}
                                    </span>
                                    <span className="text-p2w-gray/60">ganancia</span>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider text-p2w-gray">a reclamar</span>
                            </div>
                            {lowLiquidity && (
                                <div className="px-5 py-2.5 border-t border-p2w-amber/30 bg-p2w-amber/10 text-[11px] text-p2w-amber/90 leading-snug" data-testid="low-liquidity-notice">
                                    <span className="font-bold">Ganancia mínima garantizada (1%).</span> Apostaste a una opción con mucho pool —
                                    la plataforma garantiza al menos +1% si ganas.
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setStakeOpen(false)} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white">Cancelar</Button>
                        <Button onClick={onSubmit} disabled={submitting} data-testid="confirm-stake-btn" className={`${PRIMARY} font-semibold`}>
                            {submitting ? "Procesando…" : "Confirmar predicción"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <InsufficientBalanceModal
                open={insufficientOpen}
                onOpenChange={setInsufficientOpen}
                currentBalance={user?.points_balance || 0}
                requiredAmount={stakeNum}
                intent={{ market_id: market.id, option_id: selected, stake: stakeNum }}
            />
        </div>
    );
}

function StatPill({ icon: Icon, value, color, testid }) {
    const styles = {
        green: "border-p2w-green/40 bg-p2w-green/10 text-p2w-green",
        red: "border-p2w-red/40 bg-p2w-red/10 text-p2w-red",
        gray: "border-p2w-line bg-p2w-elevated text-white",
        amber: "border-p2w-amber/40 bg-p2w-amber/10 text-p2w-amber",
    };
    return (
        <div data-testid={testid} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${styles[color]}`}>
            <Icon className="w-3.5 h-3.5" />
            <span className="font-sora font-bold text-sm number-tabular">{value}</span>
        </div>
    );
}

function SentimentBar({ market, totalParticipants }) {
    if (market.options.length < 2) return null;
    return (
        <div className="mt-5">
            <div className="flex h-2 rounded-full overflow-hidden border border-p2w-line">
                {market.options.map((o, i) => {
                    const pct = totalParticipants > 0 ? ((o.participants || 0) / totalParticipants) * 100 : 100 / market.options.length;
                    const colors = ["bg-p2w-blue", "bg-p2w-purple", "bg-p2w-magenta", "bg-p2w-amber", "bg-p2w-green"];
                    return <div key={o.id} className={colors[i % colors.length]} style={{ width: `${pct}%` }} />;
                })}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {market.options.map((o, i) => {
                    const pct = totalParticipants > 0 ? ((o.participants || 0) / totalParticipants) * 100 : 100 / market.options.length;
                    const dotColors = ["bg-p2w-blue", "bg-p2w-purple", "bg-p2w-magenta", "bg-p2w-amber", "bg-p2w-green"];
                    return (
                        <div key={o.id} className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${dotColors[i % dotColors.length]}`} />
                            <span className="text-p2w-gray truncate max-w-[140px]">{o.label}</span>
                            <span className="font-sora font-bold text-p2w-blue number-tabular">{pct.toFixed(0)}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function BreakdownLine({ icon, pct, amount, label, color, testid }) {
    return (
        <div data-testid={testid} className="flex items-center justify-between py-2.5 border-b border-p2w-line last:border-b-0">
            <div className="flex items-center gap-2">
                <span className="text-base">{icon}</span>
                <span className={`font-sora font-bold ${color} number-tabular text-sm`}>{Math.round(pct * 100)}%</span>
                <span className="text-p2w-gray">→</span>
                <span className="text-white text-sm">{label}</span>
            </div>
            <span className="font-sora font-bold text-white number-tabular">{formatPesos(amount)}</span>
        </div>
    );
}

function FooterStat({ label, value, color, hideOnMobile }) {
    return (
        <div className={hideOnMobile ? "hidden sm:block" : ""}>
            <div className={`font-sora font-bold text-lg number-tabular ${color}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-p2w-gray -mt-0.5">{label}</div>
        </div>
    );
}

function ComingSoon({ icon: Icon, title, desc }) {
    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-10 text-center">
            <Icon className="w-10 h-10 text-p2w-gray mx-auto mb-3" />
            <h3 className="font-sora font-bold text-lg text-white">{title}</h3>
            <p className="mt-2 text-sm text-p2w-gray">{desc}</p>
            <span className="inline-block mt-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest text-p2w-amber bg-p2w-amber/10 border border-p2w-amber/40 font-bold">
                Próximamente
            </span>
        </div>
    );
}

export { MarketDetail };

const DONUT_COLORS = ["#9FE80D", "#7C3AED", "#3B82F6", "#D6249F", "#F5A623", "#F0454B", "#06B6D4", "#A855F7"];

function OptionsDonut({ options, totalParticipants }) {
    if (!options || options.length < 2) return null;
    const total = totalParticipants || options.reduce((s, o) => s + (o.participants || 0), 0);
    const usePool = total === 0;
    const baseTotal = usePool ? options.reduce((s, o) => s + (o.current_pool || 0), 0) : total;
    if (baseTotal <= 0) {
        return (
            <div className="hidden lg:flex flex-col items-center justify-center text-center border border-dashed border-p2w-line rounded-xl bg-p2w-elevated/40 p-4 h-full min-h-[180px]">
                <div className="w-32 h-32 rounded-full border-[14px] border-p2w-line/60" />
                <div className="text-[10px] text-p2w-gray mt-3">Sin votos aún</div>
            </div>
        );
    }

    const size = 160;
    const stroke = 22;
    const r = (size - stroke) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * r;

    let offset = 0;
    const segments = options.map((o, i) => {
        const val = usePool ? (o.current_pool || 0) : (o.participants || 0);
        const pct = val / baseTotal;
        const length = pct * circumference;
        const seg = {
            id: o.id,
            label: o.label,
            color: DONUT_COLORS[i % DONUT_COLORS.length],
            pct,
            dasharray: `${length} ${circumference - length}`,
            dashoffset: -offset,
        };
        offset += length;
        return seg;
    });

    const leader = [...options].sort((a, b) =>
        (usePool ? (b.current_pool || 0) - (a.current_pool || 0) : (b.participants || 0) - (a.participants || 0))
    )[0];
    const leaderPct = Math.round(
        ((usePool ? (leader.current_pool || 0) : (leader.participants || 0)) / baseTotal) * 100
    );

    return (
        <div className="flex flex-col items-center" data-testid="options-donut">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="-rotate-90">
                    <circle cx={cx} cy={cy} r={r} stroke="#262838" strokeWidth={stroke} fill="none" />
                    {segments.map((s) => (
                        <circle
                            key={s.id}
                            cx={cx}
                            cy={cy}
                            r={r}
                            stroke={s.color}
                            strokeWidth={stroke}
                            fill="none"
                            strokeDasharray={s.dasharray}
                            strokeDashoffset={s.dashoffset}
                        />
                    ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-sora font-extrabold text-white text-2xl number-tabular">{leaderPct}%</div>
                    <div className="text-[9px] uppercase tracking-widest text-p2w-gray">Líder</div>
                </div>
            </div>
            <div className="mt-3 space-y-1 w-full">
                {segments.slice(0, 4).map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-[10px]">
                        <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} />
                        <span className="text-white/80 truncate flex-1">{s.label}</span>
                        <span className="text-p2w-gray number-tabular">{Math.round(s.pct * 100)}%</span>
                    </div>
                ))}
                {options.length > 4 && (
                    <div className="text-[10px] text-p2w-gray text-center pt-1">
                        +{options.length - 4} más
                    </div>
                )}
            </div>
            <div className="text-[9px] text-p2w-gray mt-2 uppercase tracking-widest">
                {usePool ? "por monto" : "por votos"}
            </div>
        </div>
    );
}
